import { TransactionType, ApprovalStatus } from "generated/graphql";

/**
 * Shared utilities for transaction display formatting.
 * Used by PendingApprovals and TransactionDetail components.
 */

/**
 * Map transaction type enum to human-readable display labels
 */
export function getTransactionTypeLabel(type: TransactionType): string {
  const labels: Record<TransactionType, string> = {
    [TransactionType.Captain]: "Captain Assignment",
    [TransactionType.Cut]: "Player Cut",
    [TransactionType.Ir]: "Injured Reserve",
    [TransactionType.Promote]: "Promotion",
    [TransactionType.Relegate]: "Relegation",
    [TransactionType.Reschedule]: "Match Reschedule",
    [TransactionType.Resign]: "Re-Sign",
    [TransactionType.Sign]: "Player Signing",
    [TransactionType.Sub]: "Substitute",
    [TransactionType.Trade]: "Trade",
  };
  return labels[type] || String(type);
}

/**
 * Get badge color based on transaction type for visual distinction
 */
export function getTransactionTypeColor(type: TransactionType): string {
  const colors: Record<TransactionType, string> = {
    [TransactionType.Captain]: "violet",
    [TransactionType.Cut]: "red",
    [TransactionType.Ir]: "orange",
    [TransactionType.Promote]: "green",
    [TransactionType.Relegate]: "yellow",
    [TransactionType.Reschedule]: "cyan",
    [TransactionType.Resign]: "blue",
    [TransactionType.Sign]: "teal",
    [TransactionType.Sub]: "grape",
    [TransactionType.Trade]: "indigo",
  };
  return colors[type] || "gray";
}

/**
 * Get badge color based on approval status
 */
export function getApprovalStatusColor(status: ApprovalStatus): string {
  switch (status) {
    case ApprovalStatus.Approved:
      return "green";
    case ApprovalStatus.Rejected:
      return "red";
    case ApprovalStatus.Pending:
    default:
      return "orange";
  }
}

/**
 * Get approval status summary for a transaction
 */
export function getApprovalSummary(
  approvals: Array<{ approvalStatus: ApprovalStatus; isLeagueStaffApproval: boolean }> | null | undefined
) {
  if (!approvals || !Array.isArray(approvals)) {
    return { pending: 0, approved: 0, rejected: 0, total: 0 };
  }
  const pending = approvals.filter((a) => a.approvalStatus === ApprovalStatus.Pending).length;
  const approved = approvals.filter((a) => a.approvalStatus === ApprovalStatus.Approved).length;
  const rejected = approvals.filter((a) => a.approvalStatus === ApprovalStatus.Rejected).length;
  const total = approvals.length;
  return { pending, approved, rejected, total };
}
